<template>
  <!-- Request Documents Modal -->
  <div v-if="show" class="modal show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-file me-2"></i>
            Supporting Documents - {{request.referenceNumber}}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading documents...</p>
          </div>

          <div v-else>
            <!-- Request Info Summary -->
            <div class="request-summary mb-4">
              <div class="card bg-light">
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <p class="mb-1"><strong>Reference:</strong></p>
                      <p class="text-primary mb-0">{{request.referenceNumber}}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-1"><strong>LR Number:</strong></p>
                      <p class="text-info mb-0">{{request.LRNumber}}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-1"><strong>Owner:</strong></p>
                      <p class="mb-0">{{request.ownerNames}}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents List -->
            <div v-if="documents.length > 0">
              <h6 class="mb-3">
                <i class="bx bx-folder-open me-2"></i>
                Uploaded Documents ({{documents.length}})
              </h6>

              <div class="documents-grid">
                <div v-for="document in documents" :key="document.id" class="document-card mb-3">
                  <div class="card h-100">
                    <div class="card-body">
                      <div class="row align-items-center">
                        <div class="col-md-2 text-center">
                          <div class="file-icon">
                            <i :class="getFileIcon(document.file_type)" style="font-size: 2.5rem;"></i>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <h6 class="mb-1">{{document.original_filename}}</h6>
                          <p class="text-muted mb-1">
                            <small>
                              <i class="bx bx-category me-1"></i>
                              {{getDocumentCategoryName(document.document_category)}}
                            </small>
                          </p>
                          <p class="text-muted mb-1">
                            <small>
                              <i class="bx bx-file me-1"></i>
                              {{formatFileSize(document.file_size)}} • {{document.file_type.toUpperCase()}}
                            </small>
                          </p>
                          <p class="text-muted mb-0">
                            <small>
                              <i class="bx bx-calendar me-1"></i>
                              Uploaded: {{formatDate(document.uploaded_date)}}
                            </small>
                          </p>
                        </div>
                        <div class="col-md-2 text-center">
                          <span class="badge" :class="getVerificationBadge(document.verification_status)">
                            {{document.verification_status.toUpperCase()}}
                          </span>
                        </div>
                        <div class="col-md-2 text-center">
                          <div class="document-actions">
                            <button
                                @click="viewDocument(document)"
                                class="btn btn-sm btn-outline-primary me-1 mb-1"
                                title="View Document"
                            >
                              <i class="bx bx-show"></i>
                            </button>
                            <button
                                @click="downloadDocument(document)"
                                class="btn btn-sm btn-outline-success mb-1"
                                title="Download Document"
                            >
                              <i class="bx bx-download"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No Documents -->
            <div v-else class="no-documents text-center py-5">
              <i class="bx bx-file-blank text-muted" style="font-size: 4rem;"></i>
              <h6 class="text-muted mt-3">No Documents Uploaded</h6>
              <p class="text-muted">This request does not have any supporting documents.</p>
            </div>

            <!-- Document Categories Summary -->
            <div v-if="documents.length > 0" class="document-summary mt-4">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="bx bx-chart-pie me-2"></i>
                    Document Summary
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <p class="mb-1"><strong>Total Documents:</strong></p>
                      <p class="mb-0">{{documents.length}}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-1"><strong>Total Size:</strong></p>
                      <p class="mb-0">{{getTotalFileSize()}}</p>
                    </div>
                    <div class="col-md-4">
                      <p class="mb-1"><strong>Verification Status:</strong></p>
                      <div class="verification-counts">
                        <span v-for="(count, status) in getVerificationCounts()" :key="status" class="badge me-1" :class="getVerificationBadge(status)">
                          {{status}}: {{count}}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
          <button v-if="documents.length > 0" type="button" class="btn btn-primary" @click="downloadAllDocuments">
            <i class="bx bx-download me-2"></i>Download All
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { execute2, landrate } from '@/api';

export default {
  name: 'RequestDocumentsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    request: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      documents: []
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.request.reference_number) {
        this.loadDocuments();
      }
    }
  },
  methods: {
    async loadDocuments() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getUploadedDocuments");
        data.append("referenceNumber", this.request.reference_number);

        const response = await execute2(data, landrate);

        if (response.data.success) {
          this.documents = response.data.data.documents || [];
        } else {
          console.error('Failed to load documents:', response.data.message);
          this.documents = [];
        }
      } catch (error) {
        console.error('Error loading documents:', error);
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },

    getFileIcon(fileType) {
      const type = fileType.toLowerCase();
      const icons = {
        'pdf': 'bx bx-file-doc text-danger',
        'jpg': 'bx bx-image text-success',
        'jpeg': 'bx bx-image text-success',
        'png': 'bx bx-image text-success',
        'doc': 'bx bx-file-doc text-primary',
        'docx': 'bx bx-file-doc text-primary'
      };
      return icons[type] || 'bx bx-file text-secondary';
    },

    getDocumentCategoryName(category) {
      const categories = {
        'title_deed': 'Title Deed',
        'id_copy': 'ID Copy',
        'passport_copy': 'Passport Copy',
        'transfer_document': 'Transfer Document',
        'improvement_photo': 'Improvement Photo',
        'other': 'Other Document'
      };
      return categories[category] || 'Unknown Category';
    },

    getVerificationBadge(status) {
      const badges = {
        'pending': 'bg-warning text-dark',
        'verified': 'bg-success text-white',
        'rejected': 'bg-danger text-white'
      };
      return badges[status] || 'bg-secondary text-white';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD/MM/YYYY HH:mm');
    },

    getTotalFileSize() {
      const totalBytes = this.documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
      return this.formatFileSize(totalBytes);
    },

    getVerificationCounts() {
      const counts = {};
      this.documents.forEach(doc => {
        const status = doc.verification_status || 'pending';
        counts[status] = (counts[status] || 0) + 1;
      });
      return counts;
    },

    viewDocument(document) {
      // Open document in new tab/window
      if (document.download_url) {
        window.open(document.download_url, '_blank');
      } else {
        // Construct download URL
        const baseUrl = 'https://county.go.ke/uploads/property_updates/';
        const yearMonth = moment(document.uploaded_date).format('YYYY/MM');
        const url = `${baseUrl}${yearMonth}/${this.request.reference_number}/${document.stored_filename}`;
        window.open(url, '_blank');
      }
    },

    downloadDocument(document) {
      // Trigger download
      if (document.download_url) {
        const link = document.createElement('a');
        link.href = document.download_url;
        link.download = document.original_filename;
        link.click();
      } else {
        // Use API endpoint
        const data = new FormData();
        data.append("function", "downloadDocument");
        data.append("documentId", document.id);
        data.append("referenceNumber", this.request.reference_number);

        // This would typically be handled by your download API
        window.open(`/api/download-document?documentId=${document.id}&referenceNumber=${this.request.reference_number}`, '_blank');
      }
    },

    downloadAllDocuments() {
      // Download all documents (you might want to create a zip file)
      this.documents.forEach(document => {
        setTimeout(() => {
          this.downloadDocument(document);
        }, 100); // Small delay between downloads
      });
    },

    closeModal() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.document-card {
  transition: all 0.3s ease;
}

.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.file-icon {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
}

.document-actions .btn {
  min-width: 35px;
}

.verification-counts .badge {
  font-size: 11px;
}

.card {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }

  .document-card .row > div {
    margin-bottom: 10px;
  }

  .document-actions {
    text-align: center;
  }
}
</style>